import { toast } from "@/v2-ui/toast"
import { ConfirmButton } from "@/v2-backoffice-client/components/button"
import { useTranslationSetLanguageSupportedMutation } from "@/v2-backoffice-client/translation/languages/translation.setLanguageSupported.mutation"
import type { TranslationLanguageRow } from "@/v2-backoffice-client/translation/languages/translation.languages.utils"

export type TranslationLanguagesSupportToggleProps = {
  row: TranslationLanguageRow
}

function TranslationLanguagesSupportToggle(
  props: TranslationLanguagesSupportToggleProps,
) {
  const { row } = props
  const [setSupported, setSupportedState] =
    useTranslationSetLanguageSupportedMutation()

  const enabling = !row.isSupported

  async function onConfirm() {
    try {
      await setSupported({
        variables: { code: row.code, isSupported: enabling },
      })
      toast({
        title: `${row.code} is now ${enabling ? "supported" : "unsupported"}.`,
      })
    } catch (error) {
      toast({
        variant: "destructive",
        title: `Failed to ${enabling ? "enable" : "disable"} ${row.code}`,
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  // The STYLE.md gate (FUS-848): enabling a language with no STYLE.md
  // section means AI fills have no orthography authority or domain sources
  // to lean on — allowed, but the operator must opt in knowingly.
  const confirmContent = enabling
    ? row.hasStyleSection
      ? `Enable ${row.code}? The translation tooling starts filling it and the publish exports carry it. The user-facing language selector stays gated by PostHog.`
      : `${row.code} has NO section in STYLE.md, so AI translations for it are best-effort — add its orthography authority and domain sources to STYLE.md first for trustworthy fills. Enable anyway?`
    : `Disable ${row.code}? Existing translations are kept, but the tooling stops treating it as a target and it leaves the editor/queues.`

  return (
    <ConfirmButton
      size="sm"
      variant={enabling ? "primary" : "outline"}
      disabled={setSupportedState.loading}
      isLoading={setSupportedState.loading}
      content={
        enabling
          ? "Mark the language as supported (common.Languages.IsSupported)."
          : "Stop treating the language as a translation target."
      }
      confirmContent={confirmContent}
      confirmVariant={
        enabling && !row.hasStyleSection ? "destructive" : undefined
      }
      onConfirm={onConfirm}
      data-feature-code="UI_BackofficeTranslation_Languages_ToggleSupport"
    >
      {enabling ? "Enable" : "Disable"}
    </ConfirmButton>
  )
}

export default TranslationLanguagesSupportToggle
